import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SectionHeading, Button, ImageReveal } from '../components/ui';
import { services } from '../data/services';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Home.css';

// Image loader utility for CRA bundling
const getImage = (path: string) => {
  try {
    return require(`../assets/images/${path}`);
  } catch {
    return '';
  }
};

export function Home() {
  useDocumentTitle();

  // Refs for scroll reveal sections
  const introRef = useScrollReveal({ threshold: 0.1 });
  const servicesHeadingRef = useScrollReveal({ threshold: 0.1 });
  const servicesGridRef = useScrollReveal({ threshold: 0.1 });
  const philosophyRef = useScrollReveal({ threshold: 0.15 });
  const ctaRef = useScrollReveal({ threshold: 0.2 });

  // Featured services (show 3-4)
  const featuredServices = services.slice(0, 4);

  // Load hero and philosophy background images
  const heroImage = require('../assets/images/gallery/rekean2.jpg');
  const philosophyBgImage = require('../assets/images/gallery/barnes2.jpg');

  return (
    <div className="home page-enter">
      {/* ──────────────────────────────────────────────────────────────
          HERO SECTION
          ────────────────────────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-section__background">
          <div
            className="hero-section__image"
            style={{
              backgroundImage: `url(${heroImage})`,
            }}
          />
          <div className="hero-section__overlay" />
        </div>

        <div className="hero-section__content">
          <h1 className="hero-section__title">Mims Family Painting</h1>
          <p className="hero-section__tagline">Painting &amp; finishes for considered spaces</p>
        </div>

        {/* Scroll indicator */}
        <div className="hero-section__scroll-indicator">
          <span className="hero-section__scroll-line" />
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          INTRODUCTION SECTION
          ────────────────────────────────────────────────────────────── */}
      <section className="section introduction-section reveal" ref={introRef}>
        <div className="introduction-section__content">
          <SectionHeading
            eyebrow="Our approach"
            heading="Work that holds up"
            align="center"
          />

          <p className="introduction-section__text">
            Mims Family Painting works with homeowners, designers, architects, and contractors across Long Island and New York City. We take time to understand the surface, the scope, and the people using the space—then do the preparation and finish work with care.
          </p>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SERVICES PREVIEW SECTION
          ────────────────────────────────────────────────────────────── */}
      <section className="services-preview-section">
        <div className="services-preview-section__header reveal" ref={servicesHeadingRef}>
          <SectionHeading
            eyebrow="What we do"
            heading="Painting, finishes, and care"
            align="center"
            dark
          />
        </div>

        <div className="services-grid reveal" ref={servicesGridRef}>
          {featuredServices.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-card__image-wrapper">
                <ImageReveal
                  src={getImage(service.image)}
                  alt={service.title}
                  aspectRatio="16:9"
                  className="service-card__image"
                  revealThreshold={0.15}
                />
              </div>

              <div className="service-card__content">
                <h3 className="service-card__title">{service.title}</h3>
                <p className="service-card__description">{service.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="services-preview-section__footer">
          <Button
            asLink="/services"
            variant="secondary"
            size="md"
          >
            Explore All Services
          </Button>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          PHILOSOPHY/QUOTE SECTION
          ────────────────────────────────────────────────────────────── */}
      <section
        className="philosophy-section reveal"
        ref={philosophyRef}
        style={{
          backgroundImage: `url(${philosophyBgImage})`,
        }}
      >
        <div className="philosophy-section__overlay" />
        <blockquote className="philosophy-section__quote">
          <p className="philosophy-section__quote-text">
            The quality of a finish is decided long before the last coat.
          </p>
          <footer className="philosophy-section__attribution">
            — Mims Family Painting
          </footer>
        </blockquote>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          CTA SECTION
          ────────────────────────────────────────────────────────────── */}
      <section className="cta-section reveal" ref={ctaRef}>
        <div className="cta-section__content">
          <h2 className="cta-section__heading">Tell us about the work</h2>
          <p className="cta-section__description">
            Whether you have a defined scope or are still sorting through options, we can help you set a clear next step.
          </p>

          <div className="cta-section__actions">
            <Button
              asLink="/contact"
              variant="primary"
              size="lg"
            >
              Start a Conversation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

Home.displayName = 'Home';
